import { CommonModule } from '@angular/common';
import { Component, computed, input, output, signal, OnChanges } from '@angular/core';
import {
  GroupParticipation,
  ParticipantSuggestedGroupDraft,
  UnresolvedResolutionMode,
} from '@features/client/models/participant-selection.models';

@Component({
  selector: 'app-participant-item-groups-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './participant-item-groups-modal.html',
})
export class ParticipantItemGroupsModalComponent implements OnChanges {
  isOpen = input.required<boolean>();
  itemDescription = input.required<string>();
  itemQuantity = input.required<number>();
  groups = input<ParticipantSuggestedGroupDraft[]>([]);

  canceled = output<void>();
  confirm = output<ParticipantSuggestedGroupDraft[]>();

  readonly selectedMode = signal<UnresolvedResolutionMode>('HOST_RESOLVE');
  readonly localGroups = signal<ParticipantSuggestedGroupDraft[]>([]);

  ngOnChanges(): void {
    const hasGroups = this.groups().length > 0;

    this.selectedMode.set(hasGroups ? 'SUGGEST_GROUPS' : 'HOST_RESOLVE');

    this.localGroups.set(
      this.groups().length
        ? this.groups().map((g) => ({ ...g }))
        : [
            {
              localId: crypto.randomUUID(),
              size: 1,
              participation: 'INDIVIDUAL',
            },
          ],
    );
  }

  readonly groupsCount = computed(() => this.localGroups().length);

  readonly usedQuantity = computed(() =>
    this.localGroups().reduce((acc, group) => acc + group.size, 0),
  );

  readonly remainingQuantity = computed(() =>
    Math.max(0, this.itemQuantity() - this.usedQuantity()),
  );

  readonly canAddGroup = computed(() => {
    return this.groupsCount() < 2 && this.remainingQuantity() > 0;
  });

  readonly canConfirm = computed(() => {
    if (this.selectedMode() === 'HOST_RESOLVE') {
      return true;
    }
    const groups = this.localGroups();
    if (!groups.length) return false;

    const total = groups.reduce((acc, group) => acc + group.size, 0);
    return total > 0 && total <= this.itemQuantity();
  });

  addGroup(): void {
    if (!this.canAddGroup()) return;

    this.localGroups.update((groups) => [
      ...groups,
      {
        localId: crypto.randomUUID(),
        size: 1,
        participation: 'SHARED',
      },
    ]);
  }

  removeGroup(localId: string): void {
    this.localGroups.update((groups) => groups.filter((g) => g.localId !== localId));
  }

  setParticipation(localId: string, participation: GroupParticipation): void {
    this.localGroups.update((groups) =>
      groups.map((group) => (group.localId === localId ? { ...group, participation } : group)),
    );
  }

  incrementSize(localId: string): void {
    const current = this.localGroups().find((g) => g.localId === localId);
    if (!current) return;

    if (this.usedQuantity() >= this.itemQuantity()) return;

    this.localGroups.update((groups) =>
      groups.map((group) =>
        group.localId === localId ? { ...group, size: group.size + 1 } : group,
      ),
    );
  }

  decrementSize(localId: string): void {
    this.localGroups.update((groups) =>
      groups.map((group) =>
        group.localId === localId ? { ...group, size: Math.max(1, group.size - 1) } : group,
      ),
    );
  }

  onConfirm(): void {
    if (!this.canConfirm()) return;

    if (this.selectedMode() === 'HOST_RESOLVE') {
      this.confirm.emit([]);
      return;
    }

    this.confirm.emit(this.localGroups());
  }

  setMode(mode: UnresolvedResolutionMode): void {
    this.selectedMode.set(mode);
  }
}
